import { useState, useEffect } from "react";
import { motion } from "framer-motion";

// Hero "instrument panel" mock — a richer, terminal-style view: a watchlist
// sidebar, a candlestick chart with VWAP + entry/stop/target ruled lines and a
// volume strip, and a mono decision card. Clearly a mock; no real market data.
const CANDLES = [
    { x: 22, o: 118, c: 104, h: 98, l: 122, up: true, v: 14 },
    { x: 44, o: 104, c: 110, h: 98, l: 116, up: false, v: 9 },
    { x: 66, o: 110, c: 92, h: 86, l: 114, up: true, v: 20 },
    { x: 88, o: 92, c: 98, h: 84, l: 104, up: false, v: 11 },
    { x: 110, o: 98, c: 78, h: 72, l: 102, up: true, v: 26 },
    { x: 132, o: 78, c: 86, h: 70, l: 92, up: false, v: 13 },
    { x: 154, o: 86, c: 64, h: 58, l: 90, up: true, v: 30 },
    { x: 176, o: 64, c: 72, h: 56, l: 80, up: false, v: 12 },
    { x: 198, o: 72, c: 50, h: 44, l: 76, up: true, v: 34 },
    { x: 220, o: 50, c: 58, h: 42, l: 66, up: false, v: 15 },
    { x: 242, o: 58, c: 38, h: 32, l: 62, up: true, v: 38 },
];

const LEVELS = [
    { y: 46, label: "TGT", value: "192.40", color: "#3A9E7E" },
    { y: 92, label: "ENT", value: "188.15", color: "#B8892A" },
    { y: 132, label: "STP", value: "185.90", color: "#C94F4A" },
];

const WATCH = [
    { t: "MES", c: "HIGH", p: 0.62, up: true, active: true },
    { t: "MNQ", c: "MED", p: 0.41, up: true },
    { t: "NVDA", c: "MED", p: 1.24, up: true },
    { t: "AMC", c: "LOW", p: -0.18, up: false },
];

const convColor = (c) =>
    c === "HIGH" ? "#3A9E7E" : c === "MED" ? "#B8892A" : "#8A9BB2";

export const InstrumentPanel = () => {
    const [ticks, setTicks] = useState(WATCH);
    const [last, setLast] = useState({ px: "5 612.75", chg: 34.5, pct: 0.62 });

    useEffect(() => {
        const id = setInterval(() => {
            setTicks((prev) =>
                prev.map((w) => {
                    const p = Math.max(
                        -3,
                        Math.min(3, w.p + (Math.random() - 0.45) * 0.06),
                    );
                    return { ...w, p: Number(p.toFixed(2)), up: p >= 0 };
                }),
            );
            setLast(() => {
                const chg = Number((28 + Math.random() * 14).toFixed(2));
                const px = 5578.25 + chg;
                return {
                    px: px.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }),
                    chg,
                    pct: Number(((chg / px) * 100).toFixed(2)),
                };
            });
        }, 2200);
        return () => clearInterval(id);
    }, []);

    return (
        <div
            data-testid="instrument-panel"
            className="card-surface relative overflow-hidden p-3 sm:p-4"
        >
            {/* header */}
            <div className="mb-3 flex items-center justify-between border-b border-haze pb-3">
                <div className="flex items-center gap-2.5">
                    <span className="h-2 w-2 rounded-full bg-signal animate-pulse-soft" />
                    <span className="font-display text-sm font-bold tracking-wide text-text-hi">
                        MES
                    </span>
                    <span className="font-mono text-[11px] text-text-lo">
                        1m
                    </span>
                    <span className="rounded-sm border border-rule/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-rule">
                        Mock
                    </span>
                </div>
                <div className="flex items-center gap-3 font-mono text-[11px]">
                    <motion.span
                        key={last.px}
                        initial={{ opacity: 0.45 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.4 }}
                        className="text-text-hi"
                    >
                        {last.px}
                    </motion.span>
                    <span className="text-signal">
                        +{last.chg.toFixed(2)} ({last.pct}%)
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-[78px_1fr] gap-3">
                {/* watchlist sidebar */}
                <div className="hidden flex-col gap-1.5 sm:flex">
                    <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-text-lo">
                        Watchlist
                    </span>
                    {ticks.map((w) => (
                        <div
                            key={w.t}
                            className={`rounded-sm border px-1.5 py-1.5 ${
                                w.active
                                    ? "border-rule/50 bg-rule/10"
                                    : "border-haze bg-ink/40"
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[10px] font-medium text-text-hi">
                                    {w.t}
                                </span>
                                <span
                                    className="font-mono text-[8px]"
                                    style={{ color: convColor(w.c) }}
                                >
                                    {w.c}
                                </span>
                            </div>
                            <motion.span
                                key={w.p}
                                initial={{ opacity: 0.4 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.4 }}
                                className="block font-mono text-[9px]"
                                style={{ color: w.up ? "#3A9E7E" : "#C94F4A" }}
                            >
                                {w.up ? "+" : ""}
                                {w.p.toFixed(2)}%
                            </motion.span>
                        </div>
                    ))}
                </div>

                {/* chart */}
                <div className="relative">
                    <svg
                        viewBox="0 0 268 196"
                        className="h-auto w-full"
                        role="img"
                        aria-label="Mock candlestick chart with VWAP, entry, stop and target levels and a volume strip"
                    >
                        {/* grid */}
                        {[0, 1, 2, 3].map((i) => (
                            <line
                                key={i}
                                x1="0"
                                x2="268"
                                y1={40 * i + 10}
                                y2={40 * i + 10}
                                stroke="#1A2840"
                                strokeWidth="1"
                            />
                        ))}

                        {/* VWAP dashed line */}
                        <motion.path
                            d="M8 120 C 60 108, 110 96, 150 84 S 230 58, 260 50"
                            fill="none"
                            stroke="#8A9BB2"
                            strokeWidth="1"
                            strokeDasharray="3 3"
                            opacity="0.7"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 1.6, ease: "easeOut" }}
                        />

                        {/* level lines */}
                        {LEVELS.map((lv) => (
                            <line
                                key={lv.label}
                                x1="6"
                                x2="262"
                                y1={lv.y}
                                y2={lv.y}
                                stroke={lv.color}
                                strokeWidth="1"
                                strokeDasharray="2 4"
                                opacity="0.85"
                            />
                        ))}

                        {/* candles */}
                        {CANDLES.map((c, i) => {
                            const color = c.up ? "#3A9E7E" : "#C94F4A";
                            const top = Math.min(c.o, c.c);
                            const bodyH = Math.max(Math.abs(c.c - c.o), 2);
                            return (
                                <motion.g
                                    key={i}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        delay: 0.2 + i * 0.06,
                                        duration: 0.35,
                                        ease: "easeOut",
                                    }}
                                >
                                    <line
                                        x1={c.x}
                                        x2={c.x}
                                        y1={c.h}
                                        y2={c.l}
                                        stroke={color}
                                        strokeWidth="1.1"
                                    />
                                    <rect
                                        x={c.x - 4}
                                        y={top}
                                        width="8"
                                        height={bodyH}
                                        fill={color}
                                        rx="1"
                                    />
                                    {/* volume strip */}
                                    <rect
                                        x={c.x - 4}
                                        y={194 - c.v}
                                        width="8"
                                        height={c.v}
                                        fill={color}
                                        opacity="0.35"
                                        rx="1"
                                    />
                                </motion.g>
                            );
                        })}

                        {/* crosshair on last candle */}
                        <line
                            x1="242"
                            x2="242"
                            y1="10"
                            y2="160"
                            stroke="#B8892A"
                            strokeWidth="0.75"
                            strokeDasharray="1 5"
                            opacity="0.55"
                        />
                        <line
                            x1="0"
                            x2="268"
                            y1="150"
                            y2="150"
                            stroke="#1A2840"
                            strokeWidth="1"
                        />
                    </svg>

                    {/* level labels */}
                    <div className="pointer-events-none absolute inset-0">
                        {LEVELS.map((lv) => (
                            <div
                                key={lv.label}
                                className="absolute right-0 -translate-y-1/2"
                                style={{ top: `${(lv.y / 196) * 100}%` }}
                            >
                                <span
                                    className="rounded-sm px-1 py-0.5 font-mono text-[8px] font-medium"
                                    style={{
                                        color: lv.color,
                                        background: "rgba(9,17,31,0.9)",
                                    }}
                                >
                                    {lv.label} {lv.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* decision card */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.5 }}
                className="mt-3 rounded-md border border-haze bg-ink/70 p-3"
            >
                <div className="flex items-center justify-between font-mono text-[11px]">
                    <span className="text-text-lo">DECISION</span>
                    <span className="text-signal">WATCH → ENTER</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between font-mono text-[11px]">
                    <span className="text-text-lo">TICKER</span>
                    <span className="text-text-hi">MES</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between font-mono text-[11px]">
                    <span className="text-text-lo">CONVICTION</span>
                    <span className="text-rule">MEDIUM</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between font-mono text-[11px]">
                    <span className="text-text-lo">ACTIVATION</span>
                    <span className="text-text-hi">
                        188.15{" "}
                        <span className="text-text-lo">← ORB HIGH</span>
                    </span>
                </div>
                <p className="mt-2 font-mono text-[10.5px] leading-relaxed text-text-lo">
                    reason: reclaim of VWAP on rising relative volume; opening
                    range accepted; catalyst confirmed.
                </p>
            </motion.div>
        </div>
    );
};

export default InstrumentPanel;
